import { useMutation } from "@apollo/client";
import type {
  UpdateOrganizationBySlugInput,
  UpdateOrganizationPayload,
  OrganizationPatch,
  Organization,
} from "@casey/db-types";
import { ARCHIVE_ORGANIZATION } from "../gql";
import { useUpdateConnection } from "./use-update-connection";

interface ArchiveOrganizationVariables {
  input: UpdateOrganizationBySlugInput;
}

interface ArchiveOrganizationResponse {
  updateOrganizationBySlug: UpdateOrganizationPayload;
}

export interface UseArchiveOrganizationResult {
  archiveOrganization: (
    organizationSlug: string,
    userId: string,
  ) => Promise<Organization | null>;
  loading: boolean;
  error: Error | null;
}

export function useArchiveOrganization(): UseArchiveOrganizationResult {
  const [archiveOrganizationMutation, { loading, error }] = useMutation<
    ArchiveOrganizationResponse,
    ArchiveOrganizationVariables
  >(ARCHIVE_ORGANIZATION, {
    errorPolicy: "all",
    onError: (error) => {
      console.error("Organization archive error:", error);
    },
  });

  const { updateConnection } = useUpdateConnection();

  const generateArchivedSlug = (originalSlug: string): string => {
    // Generate a unique sequence using timestamp and random number
    const timestamp = Date.now().toString(36); // Base36 for shorter string
    const random = Math.random().toString(36).substring(2, 8); // 6 random chars
    const uniqueSequence = `${timestamp}${random}`;

    return `${originalSlug}-archived-${uniqueSequence}`;
  };

  const archiveOrganization = async (
    organizationSlug: string,
    userId: string,
  ): Promise<Organization | null> => {
    try {
      const archivedSlug = generateArchivedSlug(organizationSlug);

      const organizationPatch: OrganizationPatch = {
        slug: archivedSlug,
        archivedAt: new Date().toISOString(),
        archivedByUserId: userId,
      };

      const result = await archiveOrganizationMutation({
        variables: {
          input: {
            slug: organizationSlug,
            patch: organizationPatch,
          },
        },
        // Refetch user organizations to update the list
        refetchQueries: ["GetUserOrganizations"],
      });

      const archivedOrganization =
        result.data?.updateOrganizationBySlug?.organization;

      // Archive the organization's default interview invite
      // The default invite typically has connection_code = organization.slug
      if (archivedOrganization) {
        try {
          await updateConnection(organizationSlug, {
            connectionCode: archivedSlug,
            archivedAt: new Date().toISOString(),
            archivedByUserId: userId,
          });
        } catch (inviteError) {
          // Log the error but don't fail the organization archiving
          // The invite might not exist or might already be archived
          console.warn(
            "Failed to archive organization's default invite:",
            inviteError,
          );
        }
      }

      return archivedOrganization || null;
    } catch (err) {
      console.error("Error archiving organization:", err);
      throw err;
    }
  };

  return {
    archiveOrganization,
    loading,
    error: error || null,
  };
}
